import { Box, Stack, SxProps, Typography } from '@mui/material';
import { Theme } from '@mui/system';
import { ComponentProps, ReactNode, useMemo } from 'react';

import { useViewOnly } from '@/contexts/InteractionParadigm.context';
import { COLORS } from '@/styles/tokens/colors';

import { Medallion } from '../Medallion/Medallion';

export interface OversizedMetricItemProps {
  title: React.ReactNode;
  value: React.ReactNode;
  sx?: SxProps<Theme>;
  action?: JSX.Element;
  titleColor?: string;
  valueColor?: string;
  valueProps?: ComponentProps<typeof Typography>;
  // when invertLabel is true, the title will be displayed below the value
  invertLabel?: boolean;
  // when centered is true, the title and value will be center-aligned
  centered?: boolean;
  tooltip?: ReactNode;
  titleVariant?: ComponentProps<typeof Typography>['variant'];
  valueVariant?: ComponentProps<typeof Typography>['variant'];
  valueLabel?: string;
  valueLabelVariant?: ComponentProps<typeof Typography>['variant'];
  valueLabelColor?: string;
  valueLabelSpacing?: number;
  icon?: ReactNode;
  iconBackground?: string;
  iconForeground?: string;
  medallionVariant?: ComponentProps<typeof Medallion>['variant'];
}

export function OversizedMetricItem({
  title,
  value,
  sx,
  action,
  titleColor: externalTitleColor,
  valueColor: externalValueColor,
  valueProps,
  invertLabel,
  tooltip,
  titleVariant = 'body-xs',
  valueVariant = 'title-xl',
  centered,
  valueLabel,
  valueLabelVariant = 'body-xs',
  valueLabelColor,
  valueLabelSpacing,
  icon,
  iconBackground,
  iconForeground = undefined,
  medallionVariant,
}: OversizedMetricItemProps) {
  const isViewOnly = useViewOnly();

  const valueColor = useMemo(() => {
    if (externalValueColor) return externalValueColor;
    if (isViewOnly) return COLORS.PRIMITIVES.BLACK;
    return COLORS.TEXT.PRIMARY;
  }, [externalValueColor, isViewOnly]);

  const titleColor = useMemo(() => {
    if (externalTitleColor) return externalTitleColor;
    if (isViewOnly) return COLORS.PRIMITIVES.BLACK;
    return undefined;
  }, [externalTitleColor, isViewOnly]);

  return (
    <Stack direction="row" alignItems="center" spacing={2}>
      {icon && (
        <Medallion
          variant={medallionVariant}
          icon={icon}
          size={40}
          colors={{
            background: iconBackground || '',
            iconColor: iconForeground,
          }}
        />
      )}
      <Stack
        direction={invertLabel ? 'column-reverse' : 'column'}
        alignItems={centered ? 'center' : undefined}
        spacing={0.5}
        sx={sx}
      >
        <Stack direction="row" justifyContent="flex-start">
          <Typography
            variant={titleVariant}
            component="span"
            sx={titleColor ? { color: titleColor } : undefined}
          >
            {title}
          </Typography>
          {tooltip}
        </Stack>
        <Stack direction="row" spacing={valueLabelSpacing ?? 0.75}>
          <Typography
            variant={valueVariant}
            component="span"
            sx={valueColor ? { color: valueColor } : undefined}
            {...valueProps}
          >
            {value}
          </Typography>
          {valueLabel && (
            <Typography
              sx={{
                pt: 1.2,
              }}
              variant={valueLabelVariant}
              color={valueLabelColor}
            >
              {valueLabel}
            </Typography>
          )}
        </Stack>
      </Stack>
      {action && <Box ml={1}>{action}</Box>}
    </Stack>
  );
}
